import { ColliClient } from '@frontend/colli/api';
import { Colli, ColliType } from '@frontend/colli/types';
import { PackageClient } from '@frontend/package/api';
import { Package, PackageQueryParams } from '@frontend/package/types';
import { SettingKey } from '@frontend/setting/types';
import { Transaction } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useEffect, useState } from 'react';

import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useWizardTransactionCollis } from '../../hooks/useWizardTransactionCollis';
import { createPackagesForColli, resolvePudoPackageSequence } from '../../utils/ensure-transaction-collis-and-packages';
import { useScreen } from '../useScreen';

interface ViewProps {
    showColliFields: boolean;
    quantity: number;
    setQuantity: (quantity: number) => void;
    type: string | null;
    setType: (type: string | null) => void;
    comments: string;
    setComments: (comment: string) => void;
    hideCommentsHelp: boolean;
    setHideCommentsHelp: (hide: boolean) => void;
    submitDisabled: boolean;
    onSubmit: () => void;
    onBack: (type: ScreenType) => void;
}

const useEnterParcelInformation = (props: { wizard_id: string }): ViewProps => {
    const { store, onBack, updateWizardData, onSubmit: navigateToNextStep } = useScreen(props.wizard_id);
    const colliWorkflowId = store?.functionalitySettings.find((setting) => setting.key === SettingKey.COLLI_WORKFLOW_ID)?.value;
    const hasColliWorkflow = typeof colliWorkflowId === 'string' && colliWorkflowId.length > 0;

    const { transaction, syncTransaction } = useWizardTransaction({ wizardId: props.wizard_id, store });

    const { collis, setCollis, syncCollis } = useWizardTransactionCollis({
        wizardId: props.wizard_id,
        store,
        transactionId: transaction?.id || '',
        autoFetch: hasColliWorkflow && !!transaction?.id,
        onCreate: async (createdCollis) => {
            if (!store || !transaction?.account_id || !transaction?.id) return;

            const syncedTransaction = store.data.transaction as Transaction;
            const location = {
                spot_id: syncedTransaction.spot_id ?? store.data.spotId ?? undefined,
                module_id: syncedTransaction.module_id ?? store.data.moduleId ?? undefined,
                slot_id: syncedTransaction.slot_id ?? store.data.slotId ?? undefined
            };
            const packageSequence = resolvePudoPackageSequence(store);
            const newPackages: Package[] = [];

            for (const colli of createdCollis) {
                newPackages.push(...(await createPackagesForColli(syncedTransaction, colli, location, packageSequence)));
            }

            if (newPackages.length > 0) {
                updateWizardData({ packages: newPackages });
            }
        }
    });

    const [quantity, setQuantity] = useState<number>(store?.data.quantity || store?.data.collis?.length || 1);
    const [type, setType] = useState<string | null>(store?.data.type || ColliType.STANDARD);
    const [comments, setComments] = useState<string>(store?.data.comments || store?.data.transaction?.comments || '');
    const [hideCommentsHelp, setHideCommentsHelp] = useState<boolean>(true);

    useEffect(() => {
        if (!store?.data.transaction) return;

        if (store.data.quantity && !hasColliWorkflow) {
            setQuantity(store.data.quantity);
        }

        if (store.data.type && !hasColliWorkflow) {
            setType(store.data.type);
        }

        if (store.data.comments) {
            setComments(store.data.comments);
        } else if (store.data.transaction.comments) {
            setComments(store.data.transaction.comments);
        }
    }, [hasColliWorkflow, store?.data.comments, store?.data.quantity, store?.data.transaction, store?.data.type]);

    useEffect(() => {
        if (!hasColliWorkflow || collis.length === 0) return;

        setQuantity(collis.length);
        setType((collis[0] as Partial<Colli>).type || ColliType.STANDARD);
    }, [collis, hasColliWorkflow]);

    const onSubmit = async () => {
        if (!store || !props.wizard_id || !store.data.transaction) return;

        const newTransaction = await syncTransaction({ comments });

        if (hasColliWorkflow && newTransaction.account_id && newTransaction.id) {
            const isUpdated = collis.length !== quantity || collis.some((colli) => colli.type !== type);

            if (isUpdated) {
                const packagesResponse = await PackageClient.fetchPackages({ [PackageQueryParams.TRANSACTION_ID]: newTransaction.id });
                const existingPackages = packagesResponse.results || [];

                if (existingPackages.length > 0) {
                    await PackageClient.deletePackages(existingPackages.map((pkg) => pkg.id));
                    updateWizardData({ packages: [] });
                }

                const existingColliIds = collis.filter((colli) => colli.id).map((colli) => colli.id as string);
                if (existingColliIds.length > 0) {
                    await ColliClient.deleteCollis(existingColliIds);
                }

                const newColliData: Partial<Colli>[] = Array.from({ length: quantity }, (_, index) => ({
                    label: `${newTransaction.tracking_number || newTransaction.id}-${index + 1}`,
                    type: (type as ColliType) || ColliType.STANDARD,
                    index: index + 1,
                    workflow_id: colliWorkflowId as string,
                    spot_id: newTransaction.spot_id ?? store.data.spotId ?? undefined,
                    module_id: newTransaction.module_id ?? store.data.moduleId ?? undefined,
                    slot_id: newTransaction.slot_id ?? store.data.slotId ?? undefined
                }));

                await setCollis(newColliData);
                await syncCollis();
            }
        }

        updateWizardData({
            comments,
            type: hasColliWorkflow ? type : store.data.type,
            quantity: hasColliWorkflow ? quantity : store.data.quantity,
            transaction: newTransaction
        });

        const nextStep = store.steps.find((step) => step.screen === ScreenType.ENTER_PARCEL_INFORMATION && step.trigger === ScreenTrigger.SUBMIT);
        navigateToNextStep(nextStep);
    };

    return {
        showColliFields: hasColliWorkflow,
        quantity,
        setQuantity,
        type,
        setType,
        comments,
        setComments,
        hideCommentsHelp,
        setHideCommentsHelp,
        submitDisabled: !store || !props.wizard_id || !store.data.transaction,
        onSubmit,
        onBack
    };
};

export default useEnterParcelInformation;
